import { defineStore } from 'pinia'
import { cards } from '@/libs/api'
import { useUser } from '@/store/user'

export const useOob = defineStore('oob', {
  state: () => ({
    requests: [],
    isModalOpen: false,
    currentIndex: 0,
  }),

  getters: {
    hasRequests: (state) => state.requests.length > 0,
    currentRequest: (state) => state.requests[state.currentIndex] ?? null,
    count: (state) => state.requests.length,
  },

  actions: {
    /**
     * Called on app start and WS reconnect.
     * Opens modal automatically if there are active requests.
     * Skipped if the user lacks the cardOobConfirm permission.
     */
    async fetchRequests() {
      if (!useUser().can('cardOobConfirm')) return

      try {
        const { data } = await cards.getOobRequests()
        this.requests = [...data].sort(
          (a, b) => a.transaction.createdAt - b.transaction.createdAt,
        )
        if (data.length > 0) {
          this.currentIndex = 0
          this.isModalOpen = true
        }
      } catch (err) {
        console.error(err)
      }
    },

    /**
     * Called on live WS event v1.cardOob.initiate.
     * Adds request to the store and auto-opens the modal.
     * Ignored if the user lacks the cardOobConfirm permission.
     * @param {object} request
     */
    addRequest(request) {
      if (!useUser().can('cardOobConfirm')) return

      const exists = this.requests.some(
        (r) => r.requestId === request.requestId,
      )
      if (exists) {
        return
      }
      this.requests.push(request)
      this.currentIndex = this.requests.length - 1
      this.isModalOpen = true
    },

    /**
     * Removes a request by requestId (after response or expiry).
     * @param {string} requestId
     */
    removeRequest(requestId) {
      this.requests = this.requests.filter((r) => r.requestId !== requestId)

      if (this.requests.length === 0) {
        this.isModalOpen = false
        this.currentIndex = 0
      } else {
        this.currentIndex = Math.min(
          this.currentIndex,
          this.requests.length - 1,
        )
      }
    },

    /**
     * Sends approve/decline to the API, then removes the request from store.
     * @param {string} cardId
     * @param {string} requestId
     * @param {'Success'|'Rejected'} status
     */
    async respond(cardId, requestId, status) {
      await cards.respondOob(cardId, { requestId, status, type: '3ds' })
      this.removeRequest(requestId)
    },

    /**
     * Opens the modal for a specific request (e.g. when clicking Pending approvals list).
     * @param {string} requestId
     */
    openForRequest(requestId) {
      const idx = this.requests.findIndex((r) => r.requestId === requestId)
      if (idx !== -1) {
        this.currentIndex = idx
        this.isModalOpen = true
      }
    },

    openModal() {
      if (this.hasRequests) {
        this.isModalOpen = true
      }
    },

    closeModal() {
      this.isModalOpen = false
    },
  },
})
